import { useEffect, useRef, useState } from "react";
import { Dropdown, NumPadPop, SheetDialog, TextPad, TimePicker } from "../../components/shared";
import { blurOtherField } from "../../components/shared/padFocus";
import { api } from "../../api/client";
import { useAuth } from "../../auth";

import css from "./dashActions.module.css";

/**
 * Отчёт исполнителя о доп.работе (11.09, владелец): что делал, со скольки до скольки
 * работал на самом деле, сколько ушло на такси и кто это такси оплатил.
 *
 * Плательщик по умолчанию — сам отчитывающийся, но такси ему мог вызвать кто-то другой;
 * тогда выбирают этого человека, и возврат уйдёт в его расчётку.
 *
 * «Принять» живёт отдельно и раньше — она про «увидел работу», а не про «сделал».
 */
/** Что нужно отчёту от доп.работы — одинаково и во вкладке «Доп», и на дашборде. */
export interface ReportTarget {
  id: number; description: string; timeFrom: string; timeTo: string;
  taxi: number; taxiPaidBy: string | null;
  factFrom: string | null; factTo: string | null; reportText: string | null;
}

export function ExtraReportSheet(
  { open, work, onClose, onDone }: { open: boolean; work: ReportTarget | null; onClose: () => void; onDone: () => void },
) {
  const { user } = useAuth();
  const meId = user?.staffId ?? "";
  const [text, setText] = useState("");
  const [pad, setPad] = useState(false);
  const [from, setFrom] = useState(""), [to, setTo] = useState("");
  const [timeOpen, setTimeOpen] = useState(false);
  const [taxi, setTaxi] = useState(0), [taxiOpen, setTaxiOpen] = useState(false);
  const [payer, setPayer] = useState(""), [payerDd, setPayerDd] = useState<HTMLElement | null>(null);
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  /* Высота нашей клавиатуры: на неё поднимаем шит, иначе она ложится поверх (12.09). */
  const [dockH, setDockH] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const taxiRef = useRef<HTMLButtonElement>(null);

  /* На каждое открытие — то, что уже записано: отчёт можно переделать. */
  useEffect(() => {
    if (!open || !work) return;
    setText(work.reportText ?? "");
    setFrom(work.factFrom ?? work.timeFrom); setTo(work.factTo ?? work.timeTo);
    setTaxi(work.taxi); setPayer(work.taxiPaidBy || meId);
    setBusy(false); setErr(null); setDockH(0);
  }, [open, work, meId]);

  useEffect(() => {
    if (!open || people.length) return;
    api<{ results?: { id: string; name: string }[] }>("/api/extra-work/payers")
      .then((r) => setPeople(r.results ?? [])).catch(() => { /* останется только «я» */ });
  }, [open, people.length]);

  /* Шит поднялся — докручиваем поле к себе, иначе оно останется за верхним краем. */
  useEffect(() => {
    if (!dockH) return;
    const t = window.setTimeout(() => textRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 220);
    return () => window.clearTimeout(t);
  }, [dockH]);

  const dismiss = () => { if (!busy) onClose(); };
  const meName = user?.nickname || user?.name || "я";
  const payerItems = [
    ...(meId ? [{ id: meId, label: `я · ${meName}` }] : []),
    ...people.filter((p) => p.id !== meId).map((p) => ({ id: p.id, label: p.name })),
  ];
  const payerLabel = payer === meId ? "я" : (people.find((p) => p.id === payer)?.name ?? "выбрать…");

  const send = async () => {
    if (!work) return;
    setBusy(true); setErr(null);
    try {
      await api(`/api/extra-work/${work.id}/report`, {
        method: "POST",
        body: { taxi, taxi_paid_by: taxi ? payer : undefined, text: text.trim(), time_from: from, time_to: to },
      });
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Не удалось отправить отчёт");
      setBusy(false);
    }
  };

  return (
    <SheetDialog open={open} onDismiss={dismiss} ariaLabel="Отчёт о доп.работе" maxHeight="76dvh" liftBy={dockH}>
      <div className={css.sheetHead}>
        <span className={`${css.sheetTitle} ${css.tPink}`}>🔧 Отчёт о доп.работе</span>
        <span className={css.sheetDate}>{work?.description}</span>
      </div>
      <div className={css.sheetBody}>
        <div className={css.hint}>Что делал</div>
        <textarea className={css.textarea} ref={textRef} value={text} inputMode="none" readOnly={!pad}
          data-pad-host="" placeholder="Напр. поменял лампы и собрал реквизит…"
          onChange={(e) => setText(e.target.value)}
          onFocus={() => { blurOtherField(textRef.current); setPad(true); }}
          onPointerDown={() => setPad(true)} />

        <div className={css.hint}>Работал по факту</div>
        <div className={css.row}>
          <button type="button" className={css.act} onClick={() => setTimeOpen(true)}>{from}–{to} ›</button>
          {work && (from !== work.timeFrom || to !== work.timeTo)
            ? <span className={css.hint}>по плану {work.timeFrom}–{work.timeTo} · зачтёт ОО</span> : null}
        </div>

        <div className={css.hint}>Такси</div>
        <div className={css.row}>
          <button type="button" ref={taxiRef} className={`${css.act} ${taxi > 0 ? css.actPink : ""}`}
            onClick={() => setTaxiOpen(true)}>🚕 {taxi > 0 ? `₽${taxi}` : "не было"}</button>
          {taxi > 0 ? (
            <button type="button" className={`${css.act} ${payer ? css.actPink : ""}`}
              onClick={(e) => setPayerDd(e.currentTarget)}>оплатил: {payerLabel} ▾</button>
          ) : null}
        </div>
        {taxi > 0 && payer !== meId ? <div className={css.hint}>Деньги за такси вернутся тому, кто его оплатил.</div> : null}
        {err ? <div className={css.err}>{err}</div> : null}

        <TimePicker open={timeOpen} mode="range" step={5} label="Работал — с и до"
          value={from && to ? `${from}–${to}` : undefined}
          onCommit={(v) => { const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/); if (m) { setFrom(m[1]!); setTo(m[2]!); } setTimeOpen(false); }}
          onCancel={() => setTimeOpen(false)} />
        <NumPadPop open={taxiOpen} anchor={taxiRef.current} label="Такси · сколько" mode="money"
          value={taxi || ""} max={99999}
          onCommit={(v) => setTaxi(Math.max(0, Math.round(v)))}
          extraKeys={[{ label: "Такси не было", onPress: () => { setTaxiOpen(false); setTaxi(0); } }]}
          onClose={() => setTaxiOpen(false)} />
        <Dropdown open={!!payerDd} anchor={payerDd} multi={false} title="Кто оплатил такси"
          items={payerItems} selectedIds={payer ? [payer] : []}
          onCommit={(ids) => { const id = String(ids[0] ?? ""); if (id) setPayer(id); }}
          onClose={() => setPayerDd(null)} />
        <TextPad open={pad} docked hostRef={textRef} multiline numRow label="Что делал"
          value={text} onChange={setText} onDockHeight={setDockH} onClose={() => setPad(false)} />
      </div>
      <div className={css.foot}>
        <button type="button" className={`${css.btn} ${css.btnCancel}`} onClick={dismiss} disabled={busy}>Отмена</button>
        <button type="button" className={`${css.btn} ${css.btnGo}`} onClick={() => void send()} disabled={busy}>
          {busy ? "Отправляю…" : "Отчёт сдан"}
        </button>
      </div>
    </SheetDialog>
  );
}

